import { get, post } from '@/services/http';

/**
 * Topic interface for Insights Hub
 * Represents a topic with embedded measure/dimension codes
 */
export interface ITopic {
  code: string;
  display_name: string;
  description: string;
  category: 'inventory' | 'sales' | 'financial' | 'forecasting' | 'replenishment';
  icon: string;
  color?: string;
  question_count: number;
  priority_distribution: {
    high: number;
    medium: number;
    low: number;
  };
  // Required: every topic must have a measure
  measureCode: string;
  // Optional: dimension for filtering/grouping
  dimensionCode?: string;
}

/**
 * TopicModel - Warbler pattern model for managing topics
 * Provides centralized access to topic data and eliminates duplication
 */
export class TopicModel {

  /**
   * Get all topics
   * @returns Promise<ITopic[]> Array of all topics
   */
  public static async all(): Promise<ITopic[]> {
    // In the future, this could fetch from an API endpoint
    // For now, return static data
    return Promise.resolve([...this.topicsData]);
  }

  /**
   * Find a topic by code
   * @param code Topic code to search for
   * @returns Promise<ITopic | null> Found topic or null
   */
  public static async find(code: string): Promise<ITopic | null> {
    const topic = this.topicsData.find((topic) => topic.code === code);
    return Promise.resolve(topic || null);
  }

  /**
   * Find topics by category
   * @param category Category to filter by
   * @returns Promise<ITopic[]> Array of topics in the category
   */
  public static async findByCategory(category: ITopic['category']): Promise<ITopic[]> {
    const topics = this.topicsData.filter((topic) => topic.category === category);
    return Promise.resolve(topics);
  }

  /**
   * Find a topic by measure code
   * @param measureCode Measure code to search for
   * @returns Promise<ITopic | null> Found topic or null
   */
  public static async findByMeasureCode(measureCode: string): Promise<ITopic | null> {
    const topic = this.topicsData.find((topic) => topic.measureCode === measureCode);
    return Promise.resolve(topic || null);
  }

  /**
   * Get topics with specific measure and dimension codes
   * @param measureCode Measure code to filter by
   * @param dimensionCode Optional dimension code to filter by
   * @returns Promise<ITopic[]> Array of matching topics
   */
  public static async findByMeasureAndDimension(measureCode: string, dimensionCode?: string): Promise<ITopic[]> {
    let topics = this.topicsData.filter((topic) => topic.measureCode === measureCode);

    if (dimensionCode) {
      topics = topics.filter((topic) => topic.dimensionCode === dimensionCode);
    }

    return Promise.resolve(topics);
  }

  /**
   * Get topic categories with counts
   * @returns Promise<Array<{category: string, count: number, topics: ITopic[]}>>
   */
  public static async getCategoriesWithCounts(): Promise<Array<{
    category: ITopic['category'];
    count: number;
    topics: ITopic[];
  }>> {
    const categoryMap = new Map<ITopic['category'], ITopic[]>();

    // Group topics by category
    this.topicsData.forEach((topic) => {
      if (!categoryMap.has(topic.category)) {
        categoryMap.set(topic.category, []);
      }
      categoryMap.get(topic.category)!.push(topic);
    });

    // Convert to array with counts
    const categories = Array.from(categoryMap.entries()).map(([category, topics]) => ({
      category,
      count: topics.length,
      topics,
    }));

    return Promise.resolve(categories);
  }

  /**
   * Get topic statistics
   * @returns Promise<{totalTopics: number, totalQuestions: number, categoryCounts: Record<string, number>}>
   */
  public static async getStatistics(): Promise<{
    totalTopics: number;
    totalQuestions: number;
    categoryCounts: Record<ITopic['category'], number>;
  }> {
    const totalTopics = this.topicsData.length;
    const totalQuestions = this.topicsData.reduce((sum, topic) => sum + topic.question_count, 0);

    const categoryCounts = this.topicsData.reduce((acc, topic) => {
      acc[topic.category] = (acc[topic.category] || 0) + 1;
      return acc;
    }, {} as Record<ITopic['category'], number>);

    return Promise.resolve({
      totalTopics,
      totalQuestions,
      categoryCounts,
    });
  }

  /**
   * Search topics by name or description
   * @param searchTerm Search term to look for
   * @returns Promise<ITopic[]> Array of matching topics
   */
  public static async search(searchTerm: string): Promise<ITopic[]> {
    if (!searchTerm.trim()) {
      return this.all();
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    const matchingTopics = this.topicsData.filter((topic) =>
      topic.display_name.toLowerCase().includes(lowerSearchTerm) ||
      topic.description.toLowerCase().includes(lowerSearchTerm) ||
      topic.code.toLowerCase().includes(lowerSearchTerm),
    );

    return Promise.resolve(matchingTopics);
  }

  /**
   * Sort topics by specified criteria
   * @param topics Topics to sort
   * @param sortBy Sort criteria
   * @param sortOrder Sort order (asc/desc)
   * @returns Sorted array of topics
   */
  public static sortTopics(
    topics: ITopic[],
    sortBy: 'name' | 'category' | 'question_count' | 'priority' = 'name',
    sortOrder: 'asc' | 'desc' = 'asc',
  ): ITopic[] {
    const sorted = [...topics].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.display_name.toLowerCase();
          bValue = b.display_name.toLowerCase();
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'question_count':
          aValue = a.question_count;
          bValue = b.question_count;
          break;
        case 'priority':
          // Sort by high priority questions count
          aValue = a.priority_distribution.high;
          bValue = b.priority_distribution.high;
          break;
        default:
          aValue = a.display_name.toLowerCase();
          bValue = b.display_name.toLowerCase();
      }

      if (aValue < bValue) { return sortOrder === 'asc' ? -1 : 1; }
      if (aValue > bValue) { return sortOrder === 'asc' ? 1 : -1; }
      return 0;
    });

    return sorted;
  }

  /**
   * Format category name for display
   * @param category Category code
   * @returns Formatted category name
   */
  public static formatCategoryName(category: ITopic['category']): string {
    const categoryNames: Record<ITopic['category'], string> = {
      inventory: 'Inventory',
      sales: 'Sales',
      financial: 'Financial',
      forecasting: 'Forecasting',
      replenishment: 'Replenishment',
    };

    return categoryNames[category] || category.charAt(0).toUpperCase() + category.slice(1);
  }

  /**
   * Get category color for UI styling
   * @param category Category code
   * @returns Color code
   */
  public static getCategoryColor(category: ITopic['category']): string {
    const colors: Record<ITopic['category'], string> = {
      inventory: 'blue',
      sales: 'green',
      financial: 'purple',
      forecasting: 'orange',
      replenishment: 'teal',
    };

    return colors[category] || 'primary';
  }

  /**
   * Create measure object for InsightPanel
   * @param topic Topic object
   * @returns Measure object
   */
  public static getMeasureObject(topic: ITopic): any {
    return {
      code: topic.measureCode,
      name: this.formatMeasureName(topic.measureCode),
      display_name: this.formatMeasureName(topic.measureCode),
    };
  }

  /**
   * Create dimension object for InsightPanel
   * @param topic Topic object
   * @returns Dimension object
   */
  public static getDimensionObject(topic: ITopic): any {
    if (!topic.dimensionCode) {
      return {
        code: '',
        name: '',
        display_name: '',
      };
    }

    return {
      code: topic.dimensionCode,
      name: this.formatDimensionName(topic.dimensionCode),
      display_name: this.formatDimensionName(topic.dimensionCode),
    };
  }

  // Static data store for topics
  private static topicsData: ITopic[] = [
    {
      code: 'sales-overview',
      display_name: 'Sales Overview',
      description: 'Sales performance and revenue insights',
      category: 'sales',
      icon: 'mdi-chart-line',
      color: '#43A047',
      question_count: 12,
      priority_distribution: { high: 4, medium: 6, low: 2 },
      measureCode: 'sales_overview',
      dimensionCode: '',
    },
    {
      code: 'inventory-forecasting',
      display_name: 'Inventory Forecasting',
      description: 'Demand prediction and stock planning',
      category: 'forecasting',
      icon: 'mdi-trending-up',
      color: '#FB8C00',
      question_count: 8,
      priority_distribution: { high: 3, medium: 4, low: 1 },
      measureCode: 'inventory_forecasting',
      dimensionCode: '',
    },
    {
      code: 'inventory-replenishment',
      display_name: 'Inventory Replenishment',
      description: 'Reorder suggestions and optimization',
      category: 'replenishment',
      icon: 'mdi-refresh',
      color: '#00ACC1',
      question_count: 10,
      priority_distribution: { high: 2, medium: 5, low: 3 },
      measureCode: 'inventory_replenishment',
      dimensionCode: '',
    },
    {
      code: 'actual-inventory-quantity',
      display_name: 'Actual Inventory Quantity',
      description: 'Current stock levels and availability',
      category: 'inventory',
      icon: 'mdi-package-variant',
      color: '#1976D2',
      question_count: 15,
      priority_distribution: { high: 5, medium: 8, low: 2 },
      measureCode: 'Actual Inventory Quantity',
      dimensionCode: '',
    },
    {
      code: 'lost-revenue',
      display_name: 'Lost Revenue',
      description: 'Revenue loss from stockouts',
      category: 'financial',
      icon: 'mdi-currency-usd-off',
      color: '#7B1FA2',
      question_count: 6,
      priority_distribution: { high: 4, medium: 2, low: 0 },
      measureCode: 'lost_revenue',
      dimensionCode: '',
    },
    {
      code: 'stock-availability',
      display_name: 'Stock Availability',
      description: 'Stock availability rates and uptime',
      category: 'inventory',
      icon: 'mdi-check-circle',
      color: '#1976D2',
      question_count: 9,
      priority_distribution: { high: 3, medium: 4, low: 2 },
      measureCode: 'stock_availability',
      dimensionCode: '',
    },
    {
      code: 'inventory-burndown',
      display_name: 'Inventory Burndown',
      description: 'Depletion analysis and forecasting',
      category: 'forecasting',
      icon: 'mdi-chart-line-stacked',
      color: '#FB8C00',
      question_count: 7,
      priority_distribution: { high: 2, medium: 3, low: 2 },
      measureCode: 'inventory_burndown',
      dimensionCode: '',
    },
  ];

  /**
   * Format measure name for display
   * @param measureCode Measure code
   * @returns Formatted measure name
   */
  private static formatMeasureName(measureCode: string): string {
    return measureCode.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  /**
   * Format dimension name for display
   * @param dimensionCode Dimension code
   * @returns Formatted dimension name
   */
  private static formatDimensionName(dimensionCode: string): string {
    return dimensionCode.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
}

// ITopic interface is already exported above with the interface declaration
// No need for separate type export
